import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { WorkflowStepService } from '../workflow-step.service';
import { Automation, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Branch } from '../component-loader/branch-options';
import { v4 as uuidv4 } from 'uuid';
import { shareReplay, tap } from 'rxjs/operators';
import { AutomationEditorService } from '../automation-editor.service';
import { WORKFLOW_ACTIONS } from '../component-loader/action-definitions';
import { MatDialog } from '@angular/material/dialog';
import { GroupModalComponent } from '../components/group-modal/group-modal.component';
import { AutomationsSideDrawerService } from '../side-drawer/automations-side-drawer-service';
import { EXPANDED_GROUP_START_PREFIX, GROUP_STEP_PREFIX } from '../automation-graph';
import { AutomationGraphUtilitiesService } from '../automation-graph-utilities.service';
import { AutomationErrorHandlerService } from './automation-error-handler.service';
import { CurrentAutomationService } from '@galaxy/automata/shared';

export enum CopyStages {
  Unselected = 0,
  Selected = 1,
}

@Injectable({ providedIn: 'root' })
export class NodeActionsService {
  private stepToCopy: WorkflowStepInterface | null = null;
  private groupToCopy: string | null = null;
  private copyingStage$$ = new BehaviorSubject<CopyStages>(CopyStages.Unselected);
  public copyingStage$ = this.copyingStage$$.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public grouping = signal(false);
  public stepsInGroup: WritableSignal<Map<string, WorkflowStepInterface>> = signal(
    new Map<string, WorkflowStepInterface>(),
  );
  public groupStepSize = computed(() => this.stepsInGroup().size);

  constructor(
    private readonly workflowStepService: WorkflowStepService,
    private readonly editorService: AutomationEditorService,
    private readonly automationsSideDrawerService: AutomationsSideDrawerService,
    private readonly automationGraphUtilities: AutomationGraphUtilitiesService,
    private readonly automationEditorService: AutomationEditorService,
    private dialog: MatDialog,
    private readonly currentAutomationService: CurrentAutomationService,
    private readonly automationErrorHandlerService: AutomationErrorHandlerService,
  ) {}

  public delete(stepToDelete: WorkflowStepInterface, precedingSteps: string[]): Observable<boolean> {
    return this.workflowStepService.deleteStep(stepToDelete, precedingSteps);
  }

  public deleteGroup(groupId: string): Observable<boolean> {
    const automation = this.currentAutomationService.automation();
    const stepsInGroup = this.workflowStepService.getWorkflowStepsInGroup(automation, groupId);

    return this.workflowStepService
      .deleteMultipleActions(stepsInGroup)
      .pipe(tap(() => this.currentAutomationService.refreshAutomation()));
  }

  public copySelected(step: WorkflowStepInterface): void {
    this.stepToCopy = step;
    this.copyingStage$$.next(CopyStages.Selected);
  }

  public copyGroup(groupId: string): void {
    this.groupToCopy = groupId;
    this.copyingStage$$.next(CopyStages.Selected);
  }

  public removeGroupFromAutomation(automation: Automation, groupId: string): Observable<boolean> {
    return this.workflowStepService.removeGroupFromAutomation(automation, groupId);
  }

  public expandGroup(groupId: string): void {
    this.automationEditorService.expandGroup(groupId);
  }

  public collapseGroup(groupId: string): void {
    this.automationEditorService.collapseGroup(groupId);
  }

  public groupClicked(step: WorkflowStepInterface, checked: boolean): void {
    const nodes = new Set<WorkflowStepInterface>();

    if ((step?.choices?.rules?.length || 0) > 0) {
      const stepMap = this.automationEditorService.automationStepMap()!;

      this.automationGraphUtilities.getAllNodesInBranch(step?.id || '').forEach((node) => {
        if (stepMap.has(node)) {
          nodes.add(stepMap.get(node)!);
        }
      });
    } else {
      nodes.add(step);
    }

    nodes.forEach((n) => this.groupClickedHelper(n, checked));
  }

  private groupClickedHelper(step: WorkflowStepInterface, checked: boolean): void {
    if (!checked) {
      this.stepsInGroup.update((m) => {
        m.delete(step.id!);
        return new Map(m);
      });
    } else {
      this.grouping.set(true);
      this.stepsInGroup.update((m) => {
        m.set(step.id!, step);
        return new Map(m);
      });
      this.automationsSideDrawerService.close();
    }
  }

  public endGrouping(): void {
    this.grouping.set(false);
    this.stepsInGroup.update((m) => {
      m.clear();
      return new Map(m);
    });
  }

  public submitGroup(): void {
    const steps = [...this.stepsInGroup().values()];

    // Greedily pick a 'last node' based off of choices.defaultNextStep
    const lastNode = steps.find((step) => {
      // Check if step.choices.defaultNextStep is not in the group
      // In that case, we're at the end of the group (probably)
      return !steps.find((s) => s.id === step.choices?.defaultNextStep);
    });

    this.dialog.open(GroupModalComponent, {
      width: '500px',
      data: { groupName: lastNode?.name },
    });
  }

  public copyCleared(): void {
    this.stepToCopy = null;
    this.groupToCopy = null;
    this.copyingStage$$.next(CopyStages.Unselected);
  }

  public paste(branch: Branch, precedingSteps: string[], groupId: string): void {
    if (!this.stepToCopy && !this.groupToCopy) {
      return;
    }
    const name = WORKFLOW_ACTIONS[this.stepToCopy.taskDefinitionId]?.name;
    const stepId = uuidv4();
    const rules = this.stepToCopy.choices?.rules?.map((r) => ({
      ...r,
      next: '',
      variablePath: r?.variablePath?.replace(this.stepToCopy.id, stepId),
    }));
    this.workflowStepService
      .addStep(
        stepId,
        this.stepToCopy.taskDefinitionId,
        this.stepToCopy.data,
        this.stepToCopy.sleep,
        this.stepToCopy.sleepWaitConditions,
        this.stepToCopy.waitForTriggerTaskDefinitionId,
        this.stepToCopy.waitForTriggerFilter,
        rules,
        precedingSteps,
        branch,
        this.editorService.buildBestAttemptUniqueName(name),
        this.stepToCopy.customOutputParameters,
        '',
        this.stepToCopy.notes,
        groupId,
      )
      .subscribe({
        next: () => {
          this.copyCleared();
        },
        error: (err) => {
          this.automationErrorHandlerService.displayError(err, false);
          this.copyCleared();
        },
      });
  }

  public computeIsGrouped(stepId: string): boolean {
    return this.stepsInGroup().has(stepId);
  }

  public computeIsGroupable(stepId: string): boolean {
    if (this.stepsInGroup().size === 0) {
      // There are no other steps in the group
      return true;
    }
    // It's already in the group
    if (this.stepsInGroup().has(stepId)) {
      return true;
    }
    if (this.isPrevActionPartOfCheckedIfElseBranch(stepId)) {
      return false;
    }
    if (this.isNextActionPartOfCheckedIfElseBranch(stepId)) {
      return false;
    }
    if (this.nextActionHasMultipleUngroupedParents(stepId)) {
      return false;
    }
    if (this.isParentOfNestedGroupBranch(stepId)) {
      return false;
    }

    // one of its parents is in the group
    if (this.automationGraphUtilities.findPreviousActions(stepId).length > 0) {
      const res = this.automationGraphUtilities
        .findPreviousActions(stepId)
        .reduce((prev, stepId, _i, _arr) => this.stepsInGroup().has(stepId) || prev, false);
      if (res) {
        return true;
      }
    }

    // one of its children is in the group
    if (this.automationGraphUtilities.findNextActions(stepId).length > 0) {
      const res = this.automationGraphUtilities
        .findNextActions(stepId)
        .reduce((prev, stepId, _i, _arr) => this.stepsInGroup().has(stepId) || prev, false);
      if (res) {
        return true;
      }
    }

    return false;
  }

  // Checks if a step is part of a branch node with conditions for previous actions
  private isPrevActionPartOfCheckedIfElseBranch(stepId: string): boolean {
    if (this.automationGraphUtilities.traceNodeUpToTheBranchNodeIAmInside(stepId) === null) {
      const previousActions = this.automationGraphUtilities.findPreviousActions(stepId);

      for (const prevAction of previousActions) {
        const prevActionBranch = this.automationGraphUtilities.traceNodeUpToTheBranchNodeIAmInside(prevAction);

        if (
          prevActionBranch !== null &&
          this.stepsInGroup().has(prevAction) &&
          !this.stepsInGroup().has(prevActionBranch.nodeId)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  private isParentOfNestedGroupBranch(stepId: string): boolean {
    const visited = new Set<string>();
    const stepMap = this.automationEditorService.automationStepMap()!;
    if (!stepMap) {
      return false;
    }
    const currentStep = stepMap.get(stepId);

    while (currentStep) {
      if (visited.has(currentStep.id!)) {
        return false;
      }
      visited.add(currentStep.id!);
      // Check if the current step is an if/else branch
      if (currentStep.choices?.rules?.length > 0) {
        const currentParent = currentStep.id!;
        const childNodes = this.automationGraphUtilities.getAllNodesInBranch(currentParent);

        for (const childNode of childNodes) {
          if (childNode.startsWith(EXPANDED_GROUP_START_PREFIX) || childNode.startsWith(GROUP_STEP_PREFIX)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // Checks if a step is part of a branch node with conditions for next actions
  private isNextActionPartOfCheckedIfElseBranch(stepId: string): boolean {
    if (this.automationGraphUtilities.traceNodeUpToTheBranchNodeIAmInside(stepId) !== null) {
      const nextActions = this.automationGraphUtilities.findNextActions(stepId);

      for (const nextAction of nextActions) {
        const nextActionBranch = this.automationGraphUtilities.traceNodeUpToTheBranchNodeIAmInside(nextAction);

        if (nextActionBranch === null && this.stepsInGroup().has(nextAction)) {
          return true; // Disable all next nodes outside branch
        }
      }
    }
    return false;
  }

  // Checks if children of stepId have multiple parents that are not in the group
  private nextActionHasMultipleUngroupedParents(stepId: string): boolean {
    const nextActions = this.automationGraphUtilities.findNextActions(stepId);
    for (const nextAction of nextActions) {
      if (nextAction !== 'end-step' && this.stepsInGroup().has(nextAction)) {
        const nextActionParents = this.automationGraphUtilities.findPreviousActions(nextAction);
        let ungroupedParents = 0;
        for (const parent of nextActionParents) {
          if (!this.stepsInGroup().has(parent)) {
            ungroupedParents++;
          }
        }
        if (ungroupedParents > 1) {
          return true;
        }
      }
    }
    return false;
  }

  private isPartOfCheckedIfElseBranch(stepId: string): boolean {
    const stepMap = this.automationEditorService.automationStepMap()!;
    if (!stepMap) {
      return false;
    }
    const traceResult = this.automationGraphUtilities.traceNodeUpToTheBranchNodeIAmInside(stepId);
    if (!traceResult) {
      return false;
    }
    if (this.stepsInGroup().has(traceResult.nodeId)) {
      return true;
    }
    return false;
  }

  // A step can only be ungrouped if it is the edge of the group. You cannot ungroup a step that is in the middle of a group.
  public computeIsUnGroupable(stepId: string): boolean {
    // If the current step is not in the group then don't disable the checkbox
    // This only matters if a full if/else tree is not in the group at the same time
    if (!this.stepsInGroup().has(stepId)) {
      return true;
    }
    // If there is no middle step then anything can be ungrouped
    if (this.stepsInGroup().size < 3) {
      return true;
    }

    // Check if the node is part of a checked if/else branch, prevent ungrouping if part of if/else branch
    if (this.isPartOfCheckedIfElseBranch(stepId)) {
      return false;
    }

    let hasParentInGroup = false;
    let hasChildInGroup = false;

    // one of its parents is in the group
    if (this.automationGraphUtilities.findPreviousActions(stepId).length > 0) {
      hasParentInGroup = this.automationGraphUtilities
        .findPreviousActions(stepId)
        .reduce((prev, stepId, _i, _arr) => this.stepsInGroup().has(stepId) || prev, false);
    }

    //one of its children is in the group
    if (this.automationGraphUtilities.findNextActions(stepId).length > 0) {
      hasChildInGroup = this.automationGraphUtilities
        .findNextActions(stepId)
        .reduce((prev, stepId, _i, _arr) => this.stepsInGroup().has(stepId) || prev, false);
    }

    return !(hasParentInGroup && hasChildInGroup);
  }

  public isPartOfGroup(stepID: string, automation: AutomationInterface): boolean {
    const step = automation?.workflow?.find((step) => step?.id === stepID);
    return step?.groupId !== undefined;
  }

  public findStepsInGroup(groupID: string, automation: AutomationInterface): WorkflowStepInterface[] {
    return automation?.workflow?.filter((step) => step?.groupId === groupID) || [];
  }
}
